import React, { useState } from 'react';
import { CATEGORIES } from '../data';
import { motion } from 'motion/react';
import { 
  Terminal, Cpu, Database, Play, CheckCircle, 
  Layers, Shield, Mail, Server, Activity, ArrowUpRight, 
  Award, BrainCircuit, HeartHandshake, Link2, ListTodo, 
  Lock, Scale, Settings, Sparkles, Zap, Coins, Globe, 
  Key, FileText, ChevronRight, Search, CheckSquare, RefreshCw
} from 'lucide-react';

export interface NexoraX27HubProps {
  onNavigateToCategory: (title: string, id: number) => void;
  onNavigateToDiagnostic: (id: number) => void;
}

export function NexoraX27Hub({ onNavigateToCategory, onNavigateToDiagnostic }: NexoraX27HubProps) {
  // We strictly focus on Categories 1 through 27 as specified by the Director
  const sub27Categories = CATEGORIES.slice(0, 27);
  const [searchQuery, setSearchQuery] = useState('');

  // Simulation ledger stats for the header ribbon
  const [tickerVolume, setTickerVolume] = useState(2820000); // £2.82M gross
  const levyTotal = tickerVolume * 0.01; // 1% 3% Community Allocation
  const charitySplit = levyTotal / 3;
  const lifeTaxTotal = tickerVolume * 0.20; // 10% Maintenance & Hosting Fee (10% Maintenance & Hosting Fee)
  const netSecurePaymentSettlement = tickerVolume - levyTotal - lifeTaxTotal;

  // Icon mapping matching categories 1 to 27
  const iconList = [
    Shield, Key, BrainCircuit, Activity, Globe, Award, 
    Sparkles, Server, Cpu, Database, Link2, Lock, 
    Coins, Scale, FileText, Mail, Zap, ListTodo, 
    HeartHandshake, ChevronRight, Terminal, Settings
  ];
  const colorList = [
    'text-cyan-400 border-cyan-500/20 bg-cyan-950/10 hover:shadow-[0_0_15px_rgba(34,211,238,0.25)]',
    'text-emerald-400 border-emerald-500/20 bg-emerald-950/10 hover:shadow-[0_0_15px_rgba(16,185,129,0.25)]',
    'text-[#e840f3] border-fuchsia-500/20 bg-fuchsia-950/10 hover:shadow-[0_0_15px_rgba(232,64,243,0.25)]',
    'text-amber-400 border-amber-500/20 bg-amber-950/10 hover:shadow-[0_0_15px_rgba(245,158,11,0.25)]',
    'text-indigo-400 border-indigo-500/20 bg-indigo-950/10 hover:shadow-[0_0_15px_rgba(99,102,241,0.25)]',
    'text-rose-400 border-rose-500/20 bg-rose-950/10 hover:shadow-[0_0_15px_rgba(244,63,94,0.25)]',
    'text-sky-400 border-sky-500/20 bg-sky-950/10 hover:shadow-[0_0_15px_rgba(56,189,248,0.25)]'
  ];

  const filteredCategories = sub27Categories.filter(cat => 
    cat.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
    cat.nodes.some(node => node.toLowerCase().includes(searchQuery.toLowerCase())) ||
    `sec-${cat.id}`.includes(searchQuery.toLowerCase())
  );

  return (
    <div className="flex flex-col gap-6 w-full relative min-h-full max-w-7xl mx-auto pb-16 px-4">
      
      {/* REAL-TIME NEXORAX MASTER BANNER */}
      <div className="glass-panel border-2 border-indigo-500/30 rounded-3xl p-8 relative overflow-hidden bg-black/85 shadow-2xl">
        <div className="absolute -top-12 -right-12 p-12 opacity-[0.03] pointer-events-none">
          <Database className="w-80 h-80 text-cyan-500" />
        </div>
        
        <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-6 relative z-10">
          <div className="space-y-2">
            <span className="text-cyan-400 font-mono text-[10px] uppercase tracking-[0.4em] font-black flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-ping"></span>
              NexoraX NexoraX Cloud Ledger Grid // Active Core System Node
            </span>
            <h1 className="text-3xl md:text-4xl font-extrabold font-sans tracking-tight text-white uppercase ml-[-2px] drop-shadow-md">
              NexoraX <span className="logo-gradient-text font-black">27 Hub</span> Portal
            </h1>
            <p className="text-slate-400 font-mono text-xs max-w-3xl leading-relaxed">
              Command-and-control portal governing the 27 critical sub-categories of the Nexorax ecosystem. Instantly route system controls, spawn compliance testbeds, and audit real-time financial ledger allocations with enterprise-grade 0.8s operational synchrony.
            </p>
          </div>

          {/* DYNAMIC TAX & ROUTING TELEMETRY DECK */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 bg-black/70 border border-white/5 p-4 rounded-2xl w-full xl:w-auto min-w-[500px]">
            <div className="text-left py-1 px-2 border-r border-white/10">
              <span className="text-[8px] font-mono text-slate-500 uppercase block">Global Gross Harvest</span>
              <span className="text-xs font-black font-sans text-slate-100">£{tickerVolume.toLocaleString()}</span>
            </div>
            <div className="text-left py-1 px-2 border-r border-white/10">
              <span className="text-[8px] font-mono text-cyan-400 uppercase block">1% 3% Community Allocation</span>
              <span className="text-xs font-black font-sans text-cyan-400">£{levyTotal.toLocaleString()}</span>
            </div>
            <div className="text-left py-1 px-2 border-r border-white/10">
              <span className="text-[8px] font-mono text-amber-500 uppercase block">20% 10% Maintenance & Hosting Fee</span>
              <span className="text-xs font-black font-sans text-amber-500">£{lifeTaxTotal.toLocaleString()}</span>
            </div>
            <div className="text-left py-1 px-2">
              <span className="text-[8px] font-mono text-emerald-400 uppercase block">Net SecurePayment Settlement</span>
              <span className="text-xs font-black font-sans text-emerald-400">£{netSecurePaymentSettlement.toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* REPLICATOR STATUS TICKER */}
        <div className="mt-6 pt-4 border-t border-white/5 flex flex-wrap gap-x-8 gap-y-2 items-center text-[9px] font-mono text-slate-500 uppercase">
          <div className="flex items-center gap-2">
            <CheckSquare className="w-3.5 h-3.5 text-emerald-400" />
            <span>NexoraX Bank Sort/Account: <strong className="text-slate-300">Verified Payment Configuration | Secure Billing Provider</strong></span>
          </div>
          <div className="flex items-center gap-2">
            <Server className="w-3.5 h-3.5 text-indigo-400" />
            <span>GDrive NexoraX Resource Library Verification: <strong className="text-slate-300">CloudSync-02 [ONLINE]</strong></span>
          </div>
          <div className="flex items-center gap-2">
            <Mail className="w-3.5 h-3.5 text-pink-400" />
            <span>Inbound Entry Routing Layer: <strong className="text-slate-300">support@nexoraxdigital.co.uk</strong></span>
          </div>
          <div className="flex items-center gap-2">
            <Activity className="w-3.5 h-3.5 text-rose-400 animate-pulse" />
            <span>Active Comms Anchor: <strong className="text-slate-300">08000 622827</strong></span>
          </div>
        </div>
      </div>

      {/* QUICK COMMAND CENTER AND FILTERS */}
      <div className="flex flex-col sm:flex-row gap-4 items-center justify-between bg-black/35 backdrop-blur-md border border-indigo-500/10 p-4 rounded-2xl">
        <div className="relative group w-full sm:w-96">
          <div className="relative w-full bg-black/65 border border-white/10 group-hover:border-indigo-500/30 focus-within:border-cyan-500/40 rounded-xl leading-none flex items-center transition-all">
            <div className="p-3">
              <Search className="w-4 h-4 text-slate-500" />
            </div>
            <input 
              type="text" 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Filter NexoraX Hub categories, endpoints or IDs..."
              className="w-full bg-transparent text-slate-200 font-mono placeholder-slate-700 focus:outline-none text-[11px] py-2.5"
            />
            {searchQuery && (
              <button 
                onClick={() => setSearchQuery('')}
                className="p-3 text-[10px] font-mono text-slate-500 hover:text-white"
              >
                [CLEAR]
              </button>
            )}
          </div>
        </div>

        <div className="flex gap-4 font-mono text-[9px] uppercase tracking-wider text-slate-400">
          <div className="flex items-center gap-2 bg-emerald-500/5 border border-emerald-500/10 py-1.5 px-3 rounded-xl">
            <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />
            <span>Secure Payment Provider ACCESS COMPLIANT (NI: Secure Credential Stored Server-Side)</span>
          </div>
          <div className="flex items-center gap-2 bg-indigo-500/5 border border-indigo-500/10 py-1.5 px-3 rounded-xl">
            <Activity className="w-3.5 h-3.5 text-indigo-400" />
            <span>0.8S SENSORY LATENCY STANDARD</span>
          </div>
        </div>
      </div>

      {/* 27 DEDICATED MATRIX CARDS */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredCategories.map((cat, index) => {
          const IconComponent = iconList[index % iconList.length];
          const cosmeticStyles = colorList[index % colorList.length];
          const isCoreClass = cat.id <= 3; // Highlight core initial categories

          return (
            <motion.div
              key={cat.id}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.02, duration: 0.3 }}
              className={`glass-panel border-2 rounded-2xl p-5 flex flex-col justify-between min-h-[310px] relative transition-all group ${
                isCoreClass 
                  ? 'bg-gradient-to-b from-[#090b16] to-black border-cyan-500/25 shadow-[0_4px_30px_rgba(6,182,212,0.06)]' 
                  : 'bg-black/60 border-white/5 hover:border-indigo-500/20'
              }`}
            >
              {/* TOP STRIP BAR */}
              <div className="flex items-center justify-between mb-4">
                <span className={`font-mono text-[9px] uppercase font-black px-2.5 py-1 rounded bg-black/60 border border-white/5 ${isCoreClass ? 'text-cyan-300' : 'text-slate-500'}`}>
                  SEC-{cat.id.toString().padStart(2, '0')}
                </span>
                
                <span className="font-mono text-[8px] text-slate-500 uppercase tracking-widest flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse"></span>
                  {cat.nodes.length} Endpoints
                </span>
              </div>

              {/* CARD TITLE & COGNITIVE ICON */}
              <div className="space-y-1 ml-1 flex-1">
                <div className="flex items-start gap-3">
                  <div className={`p-2.5 rounded-xl border shrink-0 transition-all ${cosmeticStyles}`}>
                    <IconComponent className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-md font-bold font-sans tracking-tight text-slate-100 uppercase group-hover:text-cyan-300 transition-colors">
                      {cat.title}
                    </h3>
                    <p className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mt-0.5">
                      {isCoreClass ? 'Tier-1 Absolute NexoraX' : 'Sub-Category Unit Direct'}
                    </p>
                  </div>
                </div>

                {/* ENDPOINTS LISTING INSIDE CARD */}
                <div className="mt-4 bg-black/40 border border-white-[3%] rounded-xl p-3 h-28 overflow-y-auto custom-scrollbar flex flex-col gap-1.5">
                  {cat.nodes.slice(0, 4).map((node, nIdx) => (
                    <div key={nIdx} className="flex items-center gap-1 text-[9px] font-mono text-slate-400 uppercase tracking-wide">
                      <ChevronRight className="w-3 h-3 text-indigo-400 shrink-0" />
                      <span className="truncate">{node}</span>
                    </div>
                  ))}
                  {cat.nodes.length > 4 && (
                    <div className="text-[8px] font-mono text-indigo-400 uppercase tracking-widest text-right mt-1 font-extrabold">
                      + {cat.nodes.length - 4} more endpoints locked
                    </div>
                  )}
                </div>
              </div>

              {/* ACTION COMMAND BAR */}
              <div className="grid grid-cols-2 gap-2 mt-5 border-t border-white/5 pt-4">
                <button
                  onClick={() => onNavigateToCategory(cat.title, cat.id)}
                  className="px-3 py-2 border border-white/10 rounded-lg text-slate-300 hover:text-white hover:border-cyan-500/40 bg-black/30 hover:bg-black/80 font-mono text-[9px] uppercase tracking-wider font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  <Terminal className="w-3 h-3 text-cyan-400" />
                  [ INTEGRATE ]
                </button>
                <button
                  onClick={() => onNavigateToDiagnostic(cat.id)}
                  className="px-3 py-2 border border-indigo-500/20 rounded-lg text-indigo-300 hover:text-cyan-300 hover:border-cyan-400 bg-indigo-950/20 hover:bg-cyan-950/20 font-mono text-[9px] uppercase tracking-wider font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  <Activity className="w-3 h-3 text-indigo-400 group-hover:text-cyan-400" />
                  [ DIAGNOSTIC ]
                </button>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* CORE PATENT NOTICE */}
      <div className="text-center mt-12 pt-6 border-t border-white/5 max-w-2xl mx-auto space-y-1 text-[9px] font-mono text-slate-600 uppercase">
        <p>© NexoraX Digital Ltd (SC887095) // Office of the Director Lead Director</p>
        <p>All algorithm workflows, NexoraX telemetry routines, and multi-stage Tax extraction protocols are proprietary, patent-pending, and mirrored under CloudSync-02 protection.</p>
      </div>

    </div>
  );
}
